import { useCallback } from "react";
import { matchRoutes, useMatches, useNavigate } from "react-router-dom";
import { routes } from "~/components/build/Show";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { parseHash } from "~/stores/BuildPageStore";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";

/**
 * Event handler for intercepting clicks coming from vanilla links and
 * transforming them into react router navigations.
 *
 * This is useful for links that are not wrapped in a react router
 * component, for example annotations markup or within turbo frames.
 */
export const useReactRouterNavigateHandler = () => {
  const [{ pathname: basePath }] = useMatches();
  const navigate = useNavigate();

  return useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest("a");

      if (link) {
        const href = link.getAttribute("href") || "";

        try {
          const url = new URL(href, window.location.origin);

          if (url?.origin === window.location.origin) {
            const pathname = url.pathname;

            // Check if the pathname matches any of the routes
            const matches = matchRoutes(routes, pathname);
            if (pathname.includes(basePath) && matches) {
              event.preventDefault();
              event.stopPropagation();
              target.blur();

              // If the URL is a classic Buildkite job URL, resolve it to the current view
              // using the new format.
              //
              // ie. -> https://buildkite.com/org/pipeline/builds/1234#5678
              //     -> https://buildkite.com/org/pipeline/builds/1234/:current_view?jid=5678
              if (matches.pop()?.route.id === BuildView.Index) {
                const { job, group } = parseHash(url.hash) || {};

                if (job) {
                  // Open job on the current view
                  const jobUrl = resolveJobUrl(job);
                  return navigate(group ? jobUrl + url.hash : jobUrl);
                }
              }

              // If the URL has a step ID, render the step on this view.
              const search = new URLSearchParams(url.search);
              if (search.has("jid") || search.has("sid")) {
                return navigate(window.location.pathname + `?${search.toString()}` + url.hash);
              }

              return navigate(url.pathname + url.search + url.hash);
            }
          }
        } catch {
          // Either the URL is invalid or it's a non-buildkite link so continue with the default behavior.
          return;
        }
      }
    },
    [basePath, navigate],
  );
};
